import { ShipmentStatusEnum } from "./ShipmentStatus";

const TRACKING_TERMINAL_SHIPMENT_STATUSES = new Set<number>([
  ShipmentStatusEnum.DELIVERED,
  ShipmentStatusEnum.RETURNED,
  ShipmentStatusEnum.CANCELLED,
  ShipmentStatusEnum.DAMAGED,
]);

export const isTrackingTerminalStatus = (status?: number | null): boolean =>
  status != null && TRACKING_TERMINAL_SHIPMENT_STATUSES.has(status);

export type TrackingSyncErrorCode =
  | "SHIPMENT_NOT_FOUND"
  | "SHIPMENT_NOT_ELIGIBLE_FOR_TRACKING"
  | "TRACKING_SYNC_ALREADY_QUEUED"
  | "TRACKING_SYNC_RATE_LIMITED";

export type TrackingSyncIneligibilityReason =
  | "TERMINAL_SHIPMENT_STATUS"
  | "TERMINAL_TRACKING_STATUS"
  | "NOT_BOOKED"
  | "MISSING_AWB"
  | "MISSING_DELIVERY_PARTNER"
  | "TOO_OLD";

export type SingleTrackingSyncResult = {
  shipmentId: number;
  queued: boolean;
  tracking_sync_requested_at: string;
  next_sync_allowed_at: string;
};

type TrackingSyncErrorDetails = {
  reason?: TrackingSyncIneligibilityReason;
  status?: number;
  tracking_status_normalized?: string;
  retry_after_seconds?: number;
  tracking_sync_requested_at?: string;
};

const REASON_MESSAGES: Record<TrackingSyncIneligibilityReason, string> = {
  TERMINAL_SHIPMENT_STATUS:
    "This shipment is in a final status (delivered, returned, cancelled or damaged), so tracking can't be synced.",
  TERMINAL_TRACKING_STATUS:
    "Carrier tracking has already reached a final status for this shipment.",
  NOT_BOOKED: "Tracking can be synced only after the shipment is booked.",
  MISSING_AWB: "Add an AWB number before syncing tracking.",
  MISSING_DELIVERY_PARTNER: "Add a delivery partner before syncing tracking.",
  TOO_OLD:
    "Tracking can be synced only for shipments booked in the last 30 days.",
};

export function formatRetryAfter(totalSeconds: number): string {
  const seconds = Math.max(0, Math.ceil(totalSeconds));
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  if (minutes === 0) return `${remainder}s`;
  if (remainder === 0) return `${minutes} min`;
  return `${minutes} min ${remainder}s`;
}

export function getTrackingSyncErrorMessage(error: unknown): string {
  const response = (
    error as {
      response?: {
        headers?: Record<string, string | undefined>;
        data?: {
          message?: string;
          result?: {
            code?: TrackingSyncErrorCode;
            details?: TrackingSyncErrorDetails;
          };
        };
      };
    }
  )?.response;
  const data = response?.data;
  const code = data?.result?.code;
  const details = data?.result?.details;

  if (code === "TRACKING_SYNC_RATE_LIMITED") {
    const retryAfter =
      details?.retry_after_seconds ?? Number(response?.headers?.["retry-after"]);
    return Number.isFinite(retryAfter) && retryAfter > 0
      ? `Tracking sync was requested recently. Try again in ${formatRetryAfter(retryAfter)}.`
      : "Tracking sync can be requested once every 5 minutes.";
  }

  if (code === "TRACKING_SYNC_ALREADY_QUEUED") {
    return "A tracking sync for this shipment is already queued or running.";
  }

  if (code === "SHIPMENT_NOT_FOUND") {
    return "Shipment not found or no longer active.";
  }

  if (code === "SHIPMENT_NOT_ELIGIBLE_FOR_TRACKING") {
    const reason = details?.reason;
    if (reason === "TERMINAL_TRACKING_STATUS" && details?.tracking_status_normalized) {
      return `Carrier tracking has already reached a final status (${details.tracking_status_normalized}).`;
    }
    if (reason && REASON_MESSAGES[reason]) {
      return REASON_MESSAGES[reason];
    }
  }

  return (
    data?.message ||
    (error as { message?: string })?.message ||
    "Failed to queue tracking sync."
  );
}
